'use client';
import { isBefore, formatRelative } from 'date-fns';
import { enUS } from 'date-fns/locale';
import '../datetime/constants.js';
import { getTimeDifference, TimeUnit } from '../datetime/comparators.js';
import { DateFormat } from './format-date.js';

const formatRelativeTokenMap = {
    lastWeek: 'eeee',
    yesterday: "'yesterday'",
    today: "'today'",
    tomorrow: "'tomorrow'",
    nextWeek: 'eeee',
};
const getFormatRelativeLocale = (baseTokenMap, fallback) => ({
    ...enUS,
    formatRelative: (token) => baseTokenMap[token] || fallback,
});
/**
 * Format a relative date
 * @param date - The date to format
 * @param baseDate - The base date to compare against
 * @param options - The options to use
 * @param options.useGranularTime - Whether to use granular time (e.g. "1h ago" instead of "today")
 * @param options.fallback - The fallback format to use [default: DateFormat.LONG_MONTH_DAY]
 * @returns The formatted relative date
 */
const formatRelativeDate = (date, baseDate, options = {}) => {
    const { useGranularTime, fallback: fallbackOption } = options;
    if (useGranularTime) {
        const diffInMinutes = Math.abs(getTimeDifference(date, baseDate, TimeUnit.MINUTE));
        const diffInHours = Math.abs(getTimeDifference(date, baseDate, TimeUnit.HOUR));
        const diffInDays = Math.abs(getTimeDifference(date, baseDate, TimeUnit.DAY));
        const diffInWeeks = Math.abs(getTimeDifference(date, baseDate, TimeUnit.WEEK));
        let timeStr;
        if (diffInMinutes < 60) {
            timeStr = `${Math.round(diffInMinutes)}m`;
        }
        else if (diffInHours < 24) {
            timeStr = `${Math.round(diffInHours)}h`;
        }
        else if (diffInDays < 7) {
            timeStr = `${Math.round(diffInDays)}d`;
        }
        else if (diffInWeeks < 4) {
            timeStr = `${Math.round(diffInWeeks)}w`;
        }
        if (timeStr) {
            return isBefore(date, baseDate) ? `${timeStr} ago` : `in ${timeStr}`;
        }
    }
    const baseTokenMap = useGranularTime
        ? // if `useGranularTime` is true, always use `fallback` option
            {}
        : formatRelativeTokenMap;
    const fallback = fallbackOption ||
        (useGranularTime ? DateFormat.STANDARD : DateFormat.LONG_MONTH_DAY);
    return formatRelative(date, baseDate, {
        locale: getFormatRelativeLocale(baseTokenMap, fallback),
    });
};

export { formatRelativeDate };
//# sourceMappingURL=format-relative-date.js.map
